import * as React from 'react';
import {createView, view, provider} from '@sail/engine';
import {useEffect, useRef, useState} from 'react';
import {tokens as $} from '@sail/tokens';
import type {DrawerProps} from './DrawerProps';
import {BottomSheet} from '../bottom-sheet/BottomSheet';
import {useCloseBottomSheetWithConfirmation} from '../bottom-sheet/useCloseBottomSheetWithConfirmation';
import {css} from '../../css';
import {DrawerContext} from './BaseDrawer';
import {
  CreationDrawerHeader,
  type CreationDrawerHeaderProps,
} from './CreationDrawer';
import {BottomSheetContentTabs} from './BottomSheetContentTabs';
import {useCreationDrawerHeaderInChildren} from './useCreationDrawerHeaderInChildren';

export const DrawerBottomSheet = createView<DrawerProps>(
  ({
    children,
    confirmCloseMessages,
    bottomSheetVariant: preferredBottomSheetVariant,
    header,
    footer,
    onClose: closeWithoutConfirm,
    blocking = true,
    autoFocus = false,
    size = 'small',
    start,
    end,
    bottomSheetStartTabTitle,
    bottomSheetEndTabTitle,
    bottomSheetChildrenTabTitle,
    subviews,
    restoreFocus = true,
    disableDefaultBottomSheetHeader = false,
    ...props
  }) => {
    const {
      requestClose,
      closeConfirmationDialog,
      closeConfirmationDialogHeight,
    } = useCloseBottomSheetWithConfirmation(
      closeWithoutConfirm,
      confirmCloseMessages,
    );

    const [labelId, registerLabelId] = useState<string>();
    const [descriptionId, registerDescriptionId] = useState<string>();

    const contentWrapperRef = useRef<HTMLDivElement>(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);

    const headerHasMultiStepProgress =
      React.isValidElement(header) &&
      header.type === CreationDrawerHeader &&
      ((header.props as CreationDrawerHeaderProps).currentStep != null ||
        (header.props as CreationDrawerHeaderProps).totalSteps != null);

    const bottomSheetVariant =
      (headerHasMultiStepProgress ? 'fullscreen-sheet' : undefined) ??
      preferredBottomSheetVariant ??
      (size === 'small' ? 'bottom-sheet' : 'fullscreen-sheet');

    const {
      hasCreationDrawerHeaderInChildren,
      contextIntentForChildren,
      contextIntentForHeader,
    } = useCreationDrawerHeaderInChildren();

    useEffect(() => {
      if (hasCreationDrawerHeaderInChildren) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            '[@sail/ui] <CreationDrawerHeader> was found inside children. Use the `header` prop instead.',
          );
        }
      }
    }, [hasCreationDrawerHeaderInChildren]);

    const headerOrFallback =
      header ??
      (disableDefaultBottomSheetHeader ||
      hasCreationDrawerHeaderInChildren ? null : (
        <CreationDrawerHeader />
      ));

    return (
      <BottomSheet
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        {...props}
        contentWrapperRef={contentWrapperRef}
        requestClose={requestClose}
        closeConfirmationDialog={closeConfirmationDialog}
        closeConfirmationDialogHeight={closeConfirmationDialogHeight}
        layerName={blocking ? 'modal' : 'nonmodal'}
        bottomSheetVariant={bottomSheetVariant}
        subviews={{layer: subviews.layer}}
        modal={blocking}
        shouldAnimate
        hasCloseConfirmationDialog={!!confirmCloseMessages}
        // Custom props for the content wrapper
        tabIndex={-1}
        uses={[
          provider(DrawerContext, {
            onClose: requestClose,
            bottomSheetVariant,
            registerLabelId,
            registerDescriptionId,
          }),
          css({
            color: 'text',
            overflow: 'hidden',
            stack: 'y',
            focusRing: 'none',
            outline: 'none',
          }),
        ]}
      >
        {headerOrFallback ? (
          <view.div
            inherits={subviews.header}
            css={[
              css({
                height: 'fit',
                width: 'fill',
              }),
              isScrolled
                ? css({borderBlockEnd: `1px solid ${$['color.border']}`})
                : css({borderBlockEnd: 'none'}),
            ]}
            uses={[contextIntentForHeader]}
          >
            {headerOrFallback}
          </view.div>
        ) : null}
        <BottomSheetContentTabs
          contentWrapperRef={contentWrapperRef}
          setIsScrolled={setIsScrolled}
          setIsOverflowing={setIsOverflowing}
          size={size}
          subviews={{
            content: subviews?.content,
          }}
          start={start}
          end={end}
          bottomSheetStartTabTitle={bottomSheetStartTabTitle}
          bottomSheetChildrenTabTitle={bottomSheetChildrenTabTitle}
          bottomSheetEndTabTitle={bottomSheetEndTabTitle}
          uses={[contextIntentForChildren]}
        >
          {children}
        </BottomSheetContentTabs>
        {footer ? (
          <view.div
            css={[
              css({height: 'fit'}),
              isOverflowing
                ? css({borderBlockStart: `1px solid ${$['color.border']}`})
                : css({borderBlockStart: 'none'}),
            ]}
          >
            {footer}
          </view.div>
        ) : null}
      </BottomSheet>
    );
  },
);
